import { useEffect, useMemo, useRef, useState } from "react"
import { Loader2 } from "lucide-react"

import { Input } from "./input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"

export type AsyncSearchableSelectOption = {
  value: string
  label: string
}

interface AsyncSearchableSelectProps {
  value: string
  onValueChange: (value: string) => void
  fetchOptions: (search: string) => Promise<AsyncSearchableSelectOption[]>
  allOption?: AsyncSearchableSelectOption
  placeholder?: string
  searchPlaceholder?: string
  emptyPromptLabel?: string
  noResultsLabel?: string
  loadingLabel?: string
  debounceMs?: number
  disabled?: boolean
  className?: string
}

const DEFAULT_ALL_OPTION: AsyncSearchableSelectOption = { value: "ALL", label: "ทั้งหมด" }

// Like SearchableSelect, but for datasets too large to load client-side (e.g. Project No./Work
// Order No., backed by external master-data views with tens of thousands of rows): options are
// fetched from the server as the user types, debounced, instead of filtering a preloaded list.
export function AsyncSearchableSelect({
  value,
  onValueChange,
  fetchOptions,
  allOption = DEFAULT_ALL_OPTION,
  placeholder,
  searchPlaceholder = "พิมพ์เพื่อค้นหา...",
  emptyPromptLabel = "พิมพ์เพื่อค้นหา",
  noResultsLabel = "ไม่พบข้อมูล",
  loadingLabel = "กำลังโหลด...",
  debounceMs = 400,
  disabled,
  className,
}: AsyncSearchableSelectProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedTerm, setDebouncedTerm] = useState("")
  const [options, setOptions] = useState<AsyncSearchableSelectOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedOption, setSelectedOption] = useState<AsyncSearchableSelectOption | null>(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedTerm(searchTerm), debounceMs)
    return () => window.clearTimeout(handle)
  }, [searchTerm, debounceMs])

  useEffect(() => {
    const term = debouncedTerm.trim()
    if (!term) {
      setOptions([])
      setIsLoading(false)
      return
    }

    const requestId = ++requestIdRef.current
    setIsLoading(true)
    fetchOptions(term)
      .then((result) => {
        if (requestIdRef.current === requestId) {
          setOptions(result)
        }
      })
      .catch(() => {
        if (requestIdRef.current === requestId) {
          setOptions([])
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false)
        }
      })
  }, [debouncedTerm, fetchOptions])

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchTerm("")
      setDebouncedTerm("")
      setOptions([])
    }
  }

  const handleValueChange = (nextValue: string) => {
    const picked = [allOption, ...options].find((option) => option.value === nextValue) ?? null
    setSelectedOption(picked && picked.value !== allOption.value ? picked : null)
    onValueChange(nextValue)
  }

  const displayOptions = useMemo(() => {
    if (!selectedOption || options.some((option) => option.value === selectedOption.value)) {
      return options
    }
    return [selectedOption, ...options]
  }, [options, selectedOption])

  return (
    <Select value={value} onValueChange={handleValueChange} onOpenChange={handleOpenChange} disabled={disabled}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <div
          className="border-b border-gray-100 p-2"
          onKeyDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 border-gray-200"
            autoFocus
          />
        </div>
        <SelectItem value={allOption.value}>{allOption.label}</SelectItem>
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            {loadingLabel}
          </div>
        ) : displayOptions.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-gray-500">
            {debouncedTerm.trim() ? noResultsLabel : emptyPromptLabel}
          </div>
        ) : (
          displayOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  )
}
